import React, { useRef, useState } from 'react';
import { Copy, Play } from 'lucide-react';
import { Button } from './ui/button';

const VideoCard = ({ video, onCopyCode, onPlayPreview, buttonIcon = '', ctaButtonLink = 'https://onlyfans.com/my/chats/chat/960542/' }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const hasPreview = video.videoPreview && video.videoPreview.trim() !== '';

  const handleMouseEnter = () => {
    if (videoRef.current && hasPreview && !videoError) {
      setIsPlaying(true);
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
        setVideoError(true);
        setIsPlaying(false);
      });
    }
  };

  const handleMouseLeave = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleVideoError = () => {
    console.error('Video preview failed to load:', video.videoPreview);
    setVideoError(true);
  };

  const handleThumbnailError = (e) => {
    // Don't show broken image, just hide it and show gray placeholder
    e.target.style.display = 'none';
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow flex flex-col">
      {/* Thumbnail/Video Preview */}
      <div 
        className="relative aspect-video bg-gray-200 flex-shrink-0 group overflow-hidden cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => onPlayPreview(video)}
      >
        {/* Gray Placeholder (always shown as background) */}
        <div className="absolute inset-0 bg-[#e0e0e0]" />

        {/* Thumbnail Image (only rendered when URL exists to avoid empty-src warnings) */}
        {video.thumbnail && (
          <img
            src={video.thumbnail}
            alt={video.title}
            loading="lazy"
            decoding="async"
            width="640"
            height="360"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              isPlaying && hasPreview && !videoError ? 'opacity-0' : 'opacity-100'
            }`}
            onError={handleThumbnailError}
          />
        )}

        {/* Video Preview (hidden by default, shows on hover) */}
        {hasPreview && !videoError && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              isPlaying ? 'opacity-100' : 'opacity-0'
            }`}
            src={video.videoPreview}
            muted
            loop
            playsInline
            preload="none"
            onError={handleVideoError}
          />
        )}

        {/* Duration Badge - Black circle with white text */}
        <div className="absolute top-3 right-3 bg-black rounded-full px-3 py-1.5 min-w-[60px] flex items-center justify-center z-10">
          <span className="text-white text-xs font-semibold">{video.duration}</span>
        </div>
        
        {/* Play Button Overlay - Visible by default, hidden when playing */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent double trigger from parent div
            onPlayPreview(video);
          }}
          aria-label={`Play preview of ${video.title}`}
          className={`absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-all z-10 ${
            isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        >
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-sky-500 ml-1" fill="currentColor" />
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col">
        <h3 className="font-bold text-gray-900 mb-2 line-clamp-1">
          {video.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-5 overflow-hidden min-h-[6.25rem]">
          {video.description}
        </p>

        {/* Message Tag Row */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
          <span>↗ Message me: <span className="font-semibold">{video.videoCode}</span></span>
          <button
            onClick={() => onCopyCode(video.videoCode)}
            className="ml-auto p-1 hover:bg-gray-100 rounded transition-colors"
            title="Copy code"
            aria-label="Copy video code"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {/* CTA Button */}
        <Button
          className="w-full rounded-full bg-white border-2 border-sky-400 hover:bg-gray-50 text-sky-500 font-bold py-6 flex items-center justify-center gap-2 text-base tracking-wide mt-auto"
          onClick={() => window.open(ctaButtonLink, '_blank')}
        >
          <span className="font-bold text-lg">GET IT NOW ON</span>
          {buttonIcon ? (
            <img src={buttonIcon} alt="" loading="lazy" decoding="async" width="24" height="24" className="w-6 h-6 inline-block" onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <span className="text-2xl">🔥</span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default VideoCard;
